import * as React from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography,
    Tooltip,
    Chip,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import BookingDates from "../bookings/components/BookingDates";
import BookingStatusChip from "../bookings/components/BookingStatusChip";
import {
    BookingInquiryListItem,
    DEFAULT_INQUIRY_STATUSES,
    PagedResponse,
    safeStr,
    nightsBetween,
} from "./bookingInquiryTypes";

import { adminFetch } from "../../../services/adminFetch";

function useDebouncedValue<T>(value: T, delayMs: number) {
    const [v, setV] = React.useState(value);
    React.useEffect(() => {
        const t = window.setTimeout(() => setV(value), delayMs);
        return () => window.clearTimeout(t);
    }, [value, delayMs]);
    return v;
}

export default function AdminBookingInquiriesListPage() {
    const { locale } = useParams<{ locale: string }>();
    const [sp, setSp] = useSearchParams();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const q = sp.get("q") || "";
    const status = sp.get("status") || "";
    const from = sp.get("from") || "";
    const to = sp.get("to") || "";
    const limit = Math.max(1, Number(sp.get("limit") || 30));
    const offset = Math.max(0, Number(sp.get("offset") || 0));

    const [qDraft, setQDraft] = React.useState(q);
    React.useEffect(() => setQDraft(q), [q]);
    const qDebounced = useDebouncedValue(qDraft, 350);

    const [loading, setLoading] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);
    const [items, setItems] = React.useState<BookingInquiryListItem[]>([]);
    const [total, setTotal] = React.useState(0);

    const buildUrl = React.useCallback(() => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status) p.set("status", status);
        if (from) p.set("from", from);
        if (to) p.set("to", to);
        p.set("limit", String(limit));
        p.set("offset", String(offset));
        return `/api/admin/booking-inquiries?${p.toString()}`;
    }, [q, status, from, to, limit, offset]);

    const fetchList = React.useCallback(async () => {
        setLoading(true);
        setErr(null);

        const url = buildUrl();
        console.log("[Inquiries] GET:", url);

        try {
            const res = await adminFetch(url, { method: "GET" });

            const txt = await res.text();
            let j: any = {};
            try {
                j = txt ? JSON.parse(txt) : {};
            } catch {
                j = { _raw: txt };
            }

            if (!res.ok) throw new Error(j?.error || `Request failed (${res.status})`);

            const data = j as PagedResponse<BookingInquiryListItem>;
            setItems(Array.isArray(data.items) ? data.items : []);
            setTotal(typeof data.total === "number" ? data.total : 0);
        } catch (e: any) {
            setErr(e?.message || "Failed to load booking inquiries.");
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [buildUrl]);

    React.useEffect(() => {
        fetchList();
    }, [fetchList]);

    // apply debounced q -> URL params
    React.useEffect(() => {
        const current = sp.get("q") || "";
        if (qDebounced !== current) {
            const next = new URLSearchParams(sp);
            if (qDebounced) next.set("q", qDebounced);
            else next.delete("q");
            next.set("offset", "0");
            setSp(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qDebounced]);

    const setParam = (k: string, v: string) => {
        const next = new URLSearchParams(sp);
        if (v) next.set(k, v);
        else next.delete(k);
        next.set("offset", "0");
        setSp(next);
    };

    const clearFilters = () => {
        const next = new URLSearchParams();
        next.set("limit", String(limit));
        next.set("offset", "0");
        setSp(next);
        setQDraft("");
    };

    const goPage = (dir: -1 | 1) => {
        const next = new URLSearchParams(sp);
        const newOffset = Math.max(0, offset + dir * limit);
        next.set("offset", String(newOffset));
        setSp(next);
    };

    const pageFrom = total === 0 ? 0 : offset + 1;
    const pageTo = Math.min(offset + limit, total);

    return (
        <Stack spacing={2}>
            {/* Header / Filters */}
            <Paper
                elevation={0}
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    borderColor: "divider",
                    background:
                        "radial-gradient(1000px 600px at 20% -10%, rgba(156,39,176,0.10), transparent 60%), radial-gradient(900px 500px at 90% 0%, rgba(25,118,210,0.10), transparent 55%)",
                }}
            >
                <Toolbar sx={{ gap: 1.5, flexWrap: "wrap", py: 1.5 }}>
                    <Stack spacing={0}>
                        <Typography variant="h6" sx={{ fontWeight: 950, letterSpacing: 0.2 }}>
                            Booking inquiries
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            New requests from guests (website forms). Convert to bookings when confirmed.
                        </Typography>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchList} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Toolbar>

                <Divider />

                <Box sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                        <TextField
                            size="small"
                            fullWidth
                            value={qDraft}
                            onChange={(e) => setQDraft(e.target.value)}
                            placeholder="Search name / email / phone / property…"
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8 }} />,
                                endAdornment: qDraft ? (
                                    <IconButton size="small" onClick={() => setQDraft("")}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                ) : undefined,
                            }}
                        />

                        <Box
                            sx={{
                                display: "grid",
                                gap: 1.5,
                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                size="small"
                                select
                                label="Status"
                                value={status}
                                onChange={(e) => setParam("status", e.target.value)}
                            >
                                {DEFAULT_INQUIRY_STATUSES.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                size="small"
                                label="From"
                                type="date"
                                value={from}
                                onChange={(e) => setParam("from", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                size="small"
                                label="To"
                                type="date"
                                value={to}
                                onChange={(e) => setParam("to", e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />

                            <Button
                                fullWidth={isMobile}
                                onClick={clearFilters}
                                variant="text"
                                sx={{ borderRadius: 2, fontWeight: 800, justifySelf: { md: "end" } }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {err ? <Alert severity="error">{err}</Alert> : null}

            {/* Results */}
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", borderColor: "divider" }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                    gap={1}
                    sx={{ px: 2, py: 1.5 }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        Results
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Chip
                            label={total ? `${pageFrom}–${pageTo} of ${total}` : "0 results"}
                            variant="outlined"
                            sx={{ borderRadius: 999, fontWeight: 800 }}
                        />
                        <IconButton onClick={() => goPage(-1)} disabled={loading || offset === 0} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <IconButton onClick={() => goPage(1)} disabled={loading || offset + limit >= total} size="small">
                            <ArrowForwardIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <Divider />

                {/* MOBILE: Cards */}
                {isMobile ? (
                    <Box sx={{ p: 2 }}>
                        {loading ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading…
                                </Typography>
                            </Stack>
                        ) : items.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                No booking inquiries found.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {items.map((x) => {
                                    const guest = safeStr(x.full_name, "—");
                                    const email = safeStr(x.email, "");
                                    const phone = safeStr(x.phone, "");
                                    const propTitle = safeStr(
                                        x.property_title,
                                        x.property_id ? `Property #${x.property_id}` : "—",
                                    );
                                    const nights = nightsBetween(x.check_in, x.check_out);

                                    return (
                                        <Card key={x.id} variant="outlined" sx={{ borderRadius: 3 }}>
                                            <CardContent sx={{ p: 1.5 }}>
                                                <Stack spacing={1}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 950 }}>#{x.id}</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                                                {guest}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                                                {email || "—"}
                                                                {phone ? ` • ${phone}` : ""}
                                                            </Typography>
                                                        </Box>

                                                        <BookingStatusChip status={x.status ?? null} variant="outlined" />
                                                    </Stack>

                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                            {propTitle}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                                            {x.property_slug ? `/properties/${x.property_slug}` : "—"}
                                                        </Typography>
                                                    </Box>

                                                    <Stack spacing={0.25}>
                                                        <BookingDates checkIn={x.check_in} checkOut={x.check_out} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {x.guests_total ?? "—"} guests
                                                            {nights !== null ? ` • ${nights} nights` : ""}
                                                        </Typography>
                                                    </Stack>

                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            fullWidth
                                                            component={RouterLink}
                                                            to={`/${locale || "el"}/admin/booking-inquiries/${x.id}`}
                                                            size="small"
                                                            variant="contained"
                                                            sx={{ borderRadius: 2, fontWeight: 900 }}
                                                        >
                                                            View
                                                        </Button>

                                                        {x.property_slug ? (
                                                            <Tooltip title="Open property">
                                                                <IconButton
                                                                    component={RouterLink}
                                                                    to={`/${locale || "el"}/properties/${x.property_slug}`}
                                                                    size="small"
                                                                >
                                                                    <OpenInNewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                ) : (
                    /* DESKTOP: Table */
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 900 }}>ID</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Guest</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Property</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Dates</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }} align="right">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                                                <CircularProgress size={18} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Loading…
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ) : items.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                No booking inquiries found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map((x) => {
                                        const guest = safeStr(x.full_name, "—");
                                        const email = safeStr(x.email, "");
                                        const phone = safeStr(x.phone, "");
                                        const propTitle = safeStr(
                                            x.property_title,
                                            x.property_id ? `Property #${x.property_id}` : "—",
                                        );
                                        const nights = nightsBetween(x.check_in, x.check_out);

                                        return (
                                            <TableRow key={x.id} hover>
                                                <TableCell sx={{ fontWeight: 900 }}>{x.id}</TableCell>

                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                            {guest}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {email || "—"}
                                                            {phone ? ` • ${phone}` : ""}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                                            {propTitle}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {x.property_slug ? `/properties/${x.property_slug}` : "—"}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <BookingDates checkIn={x.check_in} checkOut={x.check_out} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {x.guests_total ?? "—"} guests
                                                            {nights !== null ? ` • ${nights} nights` : ""}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell>
                                                    <BookingStatusChip status={x.status ?? null} variant="outlined" />
                                                </TableCell>

                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Button
                                                            component={RouterLink}
                                                            to={`/${locale || "el"}/admin/booking-inquiries/${x.id}`}
                                                            size="small"
                                                            variant="contained"
                                                            sx={{ borderRadius: 2, fontWeight: 900 }}
                                                        >
                                                            View
                                                        </Button>

                                                        {x.property_slug ? (
                                                            <Tooltip title="Open property">
                                                                <IconButton
                                                                    component={RouterLink}
                                                                    to={`/${locale || "el"}/properties/${x.property_slug}`}
                                                                    size="small"
                                                                >
                                                                    <OpenInNewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Divider />

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    gap={1}
                    sx={{ px: 2, py: 1.5 }}
                >
                    <Typography variant="caption" color="text.secondary">
                        Tip: filter by dates to see overlapping requests quickly.
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Button
                            fullWidth={isMobile}
                            size="small"
                            variant="outlined"
                            onClick={() => goPage(-1)}
                            disabled={loading || offset === 0}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        >
                            Prev
                        </Button>
                        <Button
                            fullWidth={isMobile}
                            size="small"
                            variant="outlined"
                            onClick={() => goPage(1)}
                            disabled={loading || offset + limit >= total}
                            sx={{ borderRadius: 2, fontWeight: 800 }}
                        >
                            Next
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Stack>
    );
}
